/* istanbul ignore file */
/**
 * Created by gboue on 8/30/22
 * File: ConfigureSAML
 * Project: MSP ©Palo Alto Networks
 * © Palo Alto Networks
 */

import { VFC, useCallback, useContext, useMemo, useRef, useState, EventHandler } from 'react'
import { Body, Bold, Button, ModalFooter, Radio, RadioGroup, Select, Title } from '@panwds/react-ui'
import { PaperclipIcon } from '@panwds/icons'
import styled from 'styled-components'
import {
  BUTTON_CANCEL,
  BUTTON_CHOOSE_FILE,
  BUTTON_NEXT,
  ERROR_INVALID_URL,
  ERROR_METADATA_UPLOAD,
  ERROR_URL_METADATA,
  ERROR_URL_REQUIRED,
  LABEL_CLONE_IDP_CONFIG,
  LABEL_ENTER_MANUALLY,
  LABEL_ENTER_URL,
  LABEL_GET_URL,
  LABEL_SAML_CONFIGURE_DESC,
  LABEL_UPLOAD_METADATA,
  PLACEHOLDER_SAML_CONFIGURE_CLOUD_IDP,
  PLACEHOLDER_SAML_CONFIGURE_GET_URL,
  SAML_INSTRUCTIONS_SUBTEXT_1,
  SAML_INSTRUCTIONS_SUBTEXT_2,
  SUCCESS_DATA_UPLOADED,
  TITLE_IDP_SAML,
} from '../../../constants/LabelConstants'
import { Form, Input } from '@panwds/react-form'
import {
  DATA_TEST_SAML_CANCEL_BUTTON,
  DATA_TEST_SAML_NEXT_BUTTON,
} from '../../../../common/constants/TestingDataIds'
import { useParseFile } from '../hooks/useParseFile'
import GlobalContext from '../../../../common/context/GlobalContext'
import { useFormContext } from 'react-hook-form'
import isURL from 'validator/lib/isURL'
import { GetMetadataUrl } from '../../../actions/IAMActions'
import ScreenLoader from '../../../../common/components/screenLoader/ScreenLoader'
import { formatCert } from '../../../utils/certificate'
import type { ISamlMetadata } from '../cards/ProviderCard'
import parseXML from '../../../utils/parseXML'

const Footer: VFC<{
  loading: boolean,
  onBackPressed: EventHandler<any>,
  onNextPressed: EventHandler<any>,
  samlMetadata: ISamlMetadata | null,
  value: string,
  url: string,
  handleGetUrl: Function,
  error: boolean,
}> = ({ loading, onBackPressed, onNextPressed, samlMetadata, value, url, handleGetUrl, error }) => {
  const canSubmit = samlMetadata || value === LABEL_ENTER_MANUALLY || isURL(url)

  return (
    <ModalFooter addClassName="tw-absolute" style={{ right: 0, left: 0, bottom: 0, height: '43px' }}>
      <Button
        onClick={onBackPressed}
        appearance="secondary"
        disabled={loading}
        dataTestId={DATA_TEST_SAML_CANCEL_BUTTON}
      >{BUTTON_CANCEL}</Button>
      <Button
        appearance='primary'
        type='submit'
        disabled={loading || !canSubmit || error}
        dataTestId={DATA_TEST_SAML_NEXT_BUTTON}
      >
        {BUTTON_NEXT}
      </Button>
    </ModalFooter>
  )
}

const FORM_LAYOUT = styled.div`
   label {
     margin: 0 !important;
   }

   overflow: auto;
 `

export const BUTTON_WRAPPER = styled.div`
   justify-content: flex-end;
 `

const ConfigureSAMLForm: VFC<any> = (props) => {
  const {
    setErrorCert,
    setParsedError,
    parsedError,
    setUrl,
    setUploadedFileName,
    setSamlMetadata,
    setError,
    setValue,
    loading,
    value,
    handleFileUpload,
    handleFileUploadChange,
    hiddenFileInput,
    uploadedFileName,
    errorCert,
    error,
    samlMetadata,
    idpsDomainOptions,
    selectedItem,
    setSelectedItem,
    onBackPressed,
    onNextPressed,
    url,
    federations,
    handleGetUrl
  } = props

  const { trigger, reset, formState: { isDirty } } = useFormContext() || {}

  return (
    <>
      <FORM_LAYOUT className="tw-relative" style={{ position: 'absolute', left: 0, right: 0, top: 0, bottom: '43px' }}>
        {loading && <ScreenLoader appearance='light'/> }
        <div className="tw-flex tw-flex-col tw-p-4">
          <Title level={1} size="lg" addClassName="tw-font-semibold tw-mb-4">{TITLE_IDP_SAML}</Title>
          <Bold addClassName="tw-mb-1">{SAML_INSTRUCTIONS_SUBTEXT_1}</Bold>
          <Body addClassName="tw-text-gray-600 tw-mb-2">{SAML_INSTRUCTIONS_SUBTEXT_2}</Body>
          {/* Checkboxes */}
          <div className="tw-flex tw-gap-6 tw-p-2 tw-w-full">
            <RadioGroup name="samlConfiguration" addClassName="tw-w-full" onChange={event => {
              setUploadedFileName(null)
              setErrorCert(false)
              setParsedError(false)
              reset({
                url: ''
              })
              setSamlMetadata(null)
              setError(false)
              setValue(event.target.value)
            }}>
              <Radio value={LABEL_ENTER_MANUALLY} disabled={loading} checked={value === LABEL_ENTER_MANUALLY}><Body
                size="sm"><Bold>{LABEL_ENTER_MANUALLY}</Bold></Body></Radio>
              <div className="tw-flex tw-flex-col tw-gap-2">
                <Radio disabled={loading} value={LABEL_UPLOAD_METADATA} checked={value === LABEL_UPLOAD_METADATA}><Body
                  size="sm" addClassName="tw-mb-1 tw-mt-2"><Bold>{LABEL_UPLOAD_METADATA}</Bold></Body></Radio>
                <div className="tw-pl-6">
                  <div>
                    <Button
                      isLoading={loading}
                      size="md"
                      disabled={loading || value !== LABEL_UPLOAD_METADATA}
                      onClick={handleFileUpload} appearance="secondary"
                      icon={<PaperclipIcon size='sm' />}>{BUTTON_CHOOSE_FILE}
                    </Button>
                  </div>
                  <input
                    type="file"
                    accept=".xml"
                    onChange={(e: any) => handleFileUploadChange(e.target.files[0])}
                    ref={hiddenFileInput}
                    style={{ display: 'none' }}/>
                  {uploadedFileName && !errorCert && (
                    <Body size="xs" addClassName="tw-text-gray-600 tw-italic tw-pt-1">{uploadedFileName}</Body>
                  )}
                  {(parsedError || errorCert) && (
                    <Body size="md" addClassName="tw-text-red tw-italic tw-pt-1">{ERROR_METADATA_UPLOAD}</Body>
                  )}
                </div>
              </div>
              <div className="tw-flex tw-flex-col tw-gap-2">
                <Radio
                  disabled={loading}
                  value={LABEL_GET_URL}
                  checked={value === LABEL_GET_URL}>
                  <Body size="sm"><Bold>{LABEL_GET_URL}</Bold></Body>
                </Radio>
                <div className="tw-flex tw-gap-4 tw-pl-6">
                  <div className="tw-flex-1">
                    <Input
                      onInput={(event) => {
                        setUrl(event?.currentTarget?.value)
                      }}
                      disabled={loading || value !== LABEL_GET_URL}
                      name="url"
                      type="text"
                      placeholder={PLACEHOLDER_SAML_CONFIGURE_GET_URL}
                      required={false}
                      register={{
                        required: false,
                        onChange: async () => {
                          isDirty && setError(false)
                          await trigger('url')
                        },
                        validate: {
                          isRequired: url => (value === LABEL_GET_URL ? (!!url || ERROR_URL_REQUIRED) : true),
                          isValidUrl: url => (value === LABEL_GET_URL ? (isURL(url) || ERROR_INVALID_URL) : true)
                        }
                      }}
                      label={LABEL_ENTER_URL}
                      showLabel={false}/>
                    {error && <Body size="md" addClassName="tw-text-red tw-italic tw-pt-1">{ERROR_URL_METADATA}</Body>}
                  </div>
                </div>

              </div>
              {idpsDomainOptions?.length > 0 && (
                <div className="tw-flex tw-flex-col tw-gap-2">
                  <Radio
                    disabled={loading}
                    value={LABEL_CLONE_IDP_CONFIG}
                    checked={value === LABEL_CLONE_IDP_CONFIG}>
                    <Body size="sm" addClassName="tw-mb-1"><Bold>{LABEL_CLONE_IDP_CONFIG}</Bold></Body>
                  </Radio>
                  <div className="tw-flex tw-flex-col tw-pl-6">
                    <Select
                      addClassName='cmm-modal-select'
                      disabled={loading || value !== LABEL_CLONE_IDP_CONFIG}
                      fullWidth
                      fitToItemWidth
                      items={idpsDomainOptions}
                      placeholder={PLACEHOLDER_SAML_CONFIGURE_CLOUD_IDP}
                      selectedItem={selectedItem}
                      onChange={({ selectedItem }) => {
                        setSelectedItem(selectedItem)
                        const selectedItemIdp = federations?.items.find((f: any) => f.domain === selectedItem.value)?.idps
                        const { ssoServiceUrl: loginUrl, entityId: identityProviderId, certificates } = selectedItemIdp[0]?.config
                        setSamlMetadata({
                          loginUrl,
                          ssoUrl: loginUrl,
                          identityProviderId,
                          certificate: certificates[0],
                        })
                      }}
                      button={
                        <Button
                          addClassName="tw-w-full"
                          isMenu
                          buttonType="form-select"
                          appearance="tertiary"
                        >{selectedItem?.value}</Button>
                      }
                    />
                    <Body size="xs" addClassName="tw-text-gray-600 tw-pt-1">{LABEL_SAML_CONFIGURE_DESC}</Body>
                  </div>
                  {!errorCert && samlMetadata && (
                    <Body size="xs" addClassName="tw-text-gray-600 tw-italic tw-pt-1">{SUCCESS_DATA_UPLOADED}</Body>
                  )}
                </div>
              )}
            </RadioGroup>
          </div>
        </div>
      </FORM_LAYOUT>
      <Footer{...{ loading, onBackPressed, onNextPressed, url, samlMetadata, value, handleGetUrl, error }}/>
    </>
  )
}

const ConfigureSAML: VFC<any> = ({
  domain,
  uploadedFileName,
  setUploadedFileName,
  federations,
  setSamlMetadata,
  loading,
  setLoading,
  samlMetadata,
  onBackPressed,
  handleFile,
  setUrl,
  url,
  onNextPressed,
  error,
  setError,
}) => {
  const [items] = useState([])
  const [errorCert, setErrorCert] = useState(false)
  const [selectedItem, setSelectedItem] = useState(null)
  const [value, setValue] = useState(null)
  const { parsedError, setParsedError } = useParseFile()
  const { treeReducer } = useContext(GlobalContext)
  const { selectedTreeNode } = treeReducer

  const filteredFederations = federations?.items?.filter((f: any) => f?.idps?.length > 0).map(({ idps, domain }: any) => ({ idps, domain }))
  const idpsDomainOptions = filteredFederations.map((f: any) => {
    return { value: f?.domain }
  }).filter((d: any) => {
    return d.value !== domain
  })
  const fileReader = useMemo(() => new FileReader(), [])
  const hiddenFileInput = useRef<{ click: Function }>(null)

  const handleFileRead = useCallback((e) => {
    const content = fileReader.result
    setErrorCert(false)
    setLoading(true)
    try {
      const { loginUrl, ssoUrl, identityProviderId, certificate } = parseXML(String(content))

      setSamlMetadata({
        loginUrl,
        ssoUrl,
        identityProviderId,
        certificate,
      })
      setLoading(false)
    }
    catch (e) {
      setErrorCert(true)
      setUploadedFileName(null)
      setLoading(false)
    }
  }, [fileReader, setLoading, setSamlMetadata, setUploadedFileName])

  const handleFileUploadChange = useCallback((file) => {
    fileReader.onloadend = handleFileRead
    fileReader.readAsText(file)
    setUploadedFileName(file.name)
  }, [fileReader, handleFileRead, setUploadedFileName])

  const handleGetUrl = useCallback(async (url) => {
    const tsg_id = selectedTreeNode?.id

    if (!(isURL(url))) {
      setError(true)
      return
    }

    if (tsg_id && url) {
      setErrorCert(false)
      setLoading(true)
      try {
        const { data: { metadata } } = await GetMetadataUrl(tsg_id, domain, url, 'xml')
        const { loginUrl, ssoUrl, identityProviderId, certificate: tempCertificate } = parseXML(String(metadata))

        const certificate = formatCert(tempCertificate)

        setUploadedFileName(identityProviderId)
        setSamlMetadata({
          loginUrl,
          ssoUrl,
          identityProviderId,
          certificate,
        })
        setLoading(false)
        setError(false)
      }
      catch (e: any) {
        setError(true)
        setLoading(false)
        throw new Error(e)
      }
      setLoading(false)
    }
  }, [selectedTreeNode?.id, setError, setLoading, domain, setUploadedFileName, setSamlMetadata])

  const onSubmit = useCallback(async () => {
    try {
      url && await handleGetUrl(url)
      !error && onNextPressed()
    }
    catch (e: any) {
      setError(true)
    }
  }, [url, handleGetUrl, error, onNextPressed, setError])

  const handleFileUpload = useCallback(event => {
    hiddenFileInput?.current?.click()
  }, [])

  return (
    <Form
      mode="onBlur"
      {...{ onSubmit }}
      formProps={{ style: { height: '100%', width: '100%', position: 'relative' } }}>
      <ConfigureSAMLForm {...{
        setErrorCert,
        setParsedError,
        parsedError,
        setUploadedFileName,
        setSamlMetadata,
        setError,
        setValue,
        loading,
        value,
        handleFileUpload,
        handleFileUploadChange,
        hiddenFileInput,
        uploadedFileName,
        errorCert,
        handleGetUrl,
        error,
        samlMetadata,
        items,
        idpsDomainOptions,
        selectedItem,
        setSelectedItem,
        onBackPressed,
        onNextPressed,
        url,
        federations,
        setUrl,
      }}/>
    </Form>
  )
}

ConfigureSAML.propTypes = {}

export default ConfigureSAML

